import { type FormEvent, useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Form,
  FormErrorTypes,
  Input,
  Modal,
  useFormState,
  useFormValidation,
  ModalContext,
} from "dash-ui";
import { DashValidation, isEmptyOrNil, isNameLengthValid } from "dash-common";
import {
  useCreateMediaFolderMutation,
  useEditMediaFolderMutation,
} from "gql/generated.graphql";

enum FormFields {
  Name = "name",
}

const FormFieldsList = [FormFields.Name];

function SetFolderNameModal({
  propertyId,
  folderId,
}: {
  propertyId: string;
  folderId?: string;
}) {
  const { t } = useTranslation("forms");
  const { t: tCommonLabels } = useTranslation("common", {
    keyPrefix: "labels",
  });
  const { t: tPages } = useTranslation("pages", {
    keyPrefix: "propertyPage",
  });
  const { t: errorSlugs, i18n: i18nErrorSlugs } = useTranslation("common", {
    keyPrefix: "serverMessages",
  });
  const { isOpenCurrent, payload, toggleModal, setPayload } =
    useContext(ModalContext);
  const isOpen = !!isOpenCurrent["SetFolderNameModal"];

  const { media } = payload || {};
  const mediaFolderId = media?._id;
  const formRef = useRef<HTMLFormElement>(null);
  const [formErrors, , [setNameError], [resetFormErrors]] =
    useFormValidation(FormFieldsList);
  const [formState, [setName], [formOnSubmit, formOnError, , , resetForm]] =
    useFormState(FormFieldsList, { name: media?.folderName ?? "" });

  const [createMediaFolderMutation, { loading: loadingCreate }] =
    useCreateMediaFolderMutation();
  const [editMediaFolderMutation, { loading: loadingEdit }] =
    useEditMediaFolderMutation();
  const loading = loadingCreate || loadingEdit;

  // set name
  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    setName(media?.folderName ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media]);

  const validName = (value: string) => {
    if (isEmptyOrNil(value)) {
      setNameError({
        type: FormErrorTypes.Info,
        message: t`messages.nameBlank`,
      });
      return false;
    }
    if (!isNameLengthValid(value)) {
      setNameError({
        type: FormErrorTypes.Invalid,
        message: t("forms:messages.maxCharacterLimit", {
          number: DashValidation.MaxNameLength.toString(),
        }),
      });
      return false;
    }
    setNameError(null);
    return true;
  };

  const onValueTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    validName(e.target.value);
  };

  const onModalClose = () => {
    resetForm();
    resetFormErrors();
    toggleModal(false, "SetFolderNameModal");
    setPayload(null);
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    formOnSubmit();
    const { name } = formState;
    if (!validName(name)) {
      return null;
    }

    const onError = (error: { message: string }) => {
      const errorS = i18nErrorSlugs.exists(error.message)
        ? errorSlugs(error.message)
        : errorSlugs`unknown`;
      formOnError(errorS || "");
    };
    if (mediaFolderId) {
      return editMediaFolderMutation({
        variables: {
          input: { mediaDetails: { folderName: name }, mediaFolderId },
        },
      })
        .then(onModalClose)
        .catch(onError);
    }
    return createMediaFolderMutation({
      variables: {
        input: {
          folderName: name,
          propertyId,
          ...(folderId ? { mediaFolderId: folderId } : {}),
        },
      },
    })
      .then(onModalClose)
      .catch(onError);
  };

  return (
    <Modal
      title={
        mediaFolderId
          ? tPages`editFolderModalLabel` || ""
          : tPages`addFolderModalLabel` || ""
      }
      isOpen={isOpen}
      onRequestClose={onModalClose}
      submitButton={{
        title: tCommonLabels`save`,
        isLoading: loading,
        disabled: loading,
        onClick: () => {
          if (formRef.current) {
            formRef.current.requestSubmit();
          }
        },
      }}
      cancelButton={{ title: tCommonLabels`cancel`, onClick: onModalClose }}
    >
      <Form ref={formRef} onSubmit={onSubmit}>
        <Form.Group mt={1}>
          <Form.Label required>{t`labels.name`}</Form.Label>
          <Input
            w="100%"
            value={formState.name || ""}
            placeholder={t`placeholders.folderName` || ""}
            onChange={onValueTextChange}
            $errorType={formErrors[FormFields.Name]?.type}
          />
          <Form.Feedback $errorType={formErrors[FormFields.Name]?.type}>
            {formErrors[FormFields.Name]?.message}
          </Form.Feedback>
        </Form.Group>
        <Form.Group mt={4}>
          <Form.Feedback $errorType={FormErrorTypes.Invalid}>
            {formState.errorSlug}
          </Form.Feedback>
        </Form.Group>
      </Form>
    </Modal>
  );
}

export default SetFolderNameModal;
